/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Stepper Shell styles
 *
 * @file StepperShellStyles.js
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import { styled } from '@mui/material/styles';
import mixins from '../../../assets/styles/mixins.scss';
import { createScopedClasses, createScopedStyles } from '../../../assets/styles/createScopedStyles';
import {
  DashboardCardActionsLeftContainer,
  DashboardCardHeader,
  DashboardCardRoot
} from '../../dashboardCard/styles/DashboardCardStyles';

const stepperShellStyles = () => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    boxSizing: 'border-box'
  },
  dashboardCardRoot: {
    borderRadius: 24,
    padding: 16,
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: mixins.fontFamilyPlusSans,

    [mixins['md-sm-maxwidth']]: {
      borderRadius: 16,
      padding: 12
    }
  },
  dashboardCardHeader: {
    flexWrap: 'nowrap',
    gap: 8,

    [mixins['md-sm-maxwidth']]: {
      flexWrap: 'wrap',
      alignItems: 'stretch',
      gap: 4
    }
  },
  dashboardCardActionsLeft: {
    display: 'flex',
    flexDirection: 'row',
    flex: '1 1 auto',
    minWidth: 0,
    width: 'auto',
    maxWidth: 'none',
    flexWrap: 'nowrap',

    [mixins['md-sm-maxwidth']]: {
      flex: '1 1 100%',
      width: '100%',
      maxWidth: '100%'
    }
  },
  dashboardCardActionsRight: {
    flexShrink: 0,
    alignItems: 'flex-start',
    flexDirection: 'row',

    [mixins['md-sm-maxwidth']]: {
      flex: '1 1 100%',
      width: '100%',
      justifyContent: 'flex-end',
      alignItems: 'center'
    }
  },
  dashboardCardTitle: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',

    [mixins['md-sm-maxwidth']]: {
      fontSize: mixins.fontSize20,
      lineHeight: '28px',
      letterSpacing: '-0.4px',
      padding: '0 4px'
    },

    [mixins['ml-max']]: {
      fontSize: 18,
      lineHeight: '25px',
      letterSpacing: '-0.36px'
    }
  },
  dashboardCardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',

    [mixins['md-sm-maxwidth']]: {
      gap: 12
    }
  },
  disclaimer: {
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.greyScaleColor,
    fontFamily: mixins.fontFamilyPlusSans,
    padding: '0 16px',
    margin: 0,

    [mixins['md-sm-maxwidth']]: {
      padding: '0 8px',
      lineHeight: '18px'
    }
  },
  actionsRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    width: '100%',
    padding: '0 16px',
    boxSizing: 'border-box',

    [mixins['md-sm-maxwidth']]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: 4,
      padding: '12px 0 0'
    }
  },
  cancelButton: {
    borderRadius: 1000,
    border: `2px solid ${mixins.lightTextColor2}`,
    color: mixins.lightTextColor2,
    padding: '16px 24px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    textTransform: 'none',
    backgroundColor: 'transparent',
    maxHeight: 48,
    minWidth: 104,

    '&:hover': {
      backgroundColor: mixins.lightThemeBgColor2
    },

    [mixins['md-sm-maxwidth']]: {
      width: '100%',
      minWidth: 0,
      maxHeight: 48
    }
  },
  finishButton: {
    borderRadius: 1000,
    backgroundColor: `${mixins.amethyst} !important`,
    color: `${mixins.chalkWhiteFont} !important`,
    padding: '16px 24px !important',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: `${mixins.fontSize16} !important`,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    textTransform: 'none',
    maxHeight: 48,
    minWidth: 104,

    '&:hover': {
      backgroundColor: `${mixins.amethystColorV2} !important`,
      color: `${mixins.chalkWhiteFont} !important`
    },
    '&:disabled': {
      backgroundColor: `${mixins.amethystLightColor} !important`,
      color: `${mixins.chalkWhiteFont} !important`
    },

    [mixins['md-sm-maxwidth']]: {
      width: '100%',
      minWidth: 0,
      maxHeight: 48
    }
  }
});

export const classes = createScopedClasses('aet-stepper-shell', stepperShellStyles());

export const scopedStyles = createScopedStyles(stepperShellStyles(), classes);

export const StepperShellDashboardCardRoot = styled(DashboardCardRoot)({
  '&&': {
    backgroundColor: mixins.lightThemeBgColor2
  }
});

export const StepperShellDashboardCardActionsLeft = styled(DashboardCardActionsLeftContainer)({
  '&&': {
    alignItems: 'center',
    gap: 0
  }
});

export const StepperShellDashboardCardHeader = styled(DashboardCardHeader)({
  '&&': {
    alignItems: 'flex-start'
  }
});

export const StepperShellStylesRoot = styled('div')(scopedStyles);

export default stepperShellStyles;
